import { useState, useEffect } from 'react';
import { Plus, Search, Filter, X, Briefcase, Calendar, Archive } from 'lucide-react';
import { Select } from '../components/ui/Select';
import { useLumisial } from '../context/LumisialContext';
import { actividadService } from '../services/actividadService';
import { ActividadEconomica } from '../lib/supabase';
import { ActividadModal } from '../components/ActividadModal';
import { StatusBadge } from '../components/ui/StatusBadge';
import { formatCurrency, formatDate } from '../utils/formatters';
import { useAuth } from '../context/AuthContext';
import { puedeOperarDineroDeLumisial } from '../utils/permissions';
import { getCoachingUtilidad, calcularPorcentajeUtilidadPotencial, CoachingUtilidad } from '../utils/coachingUtilidad';

interface CoachingPanelProps {
  readonly esFinalizada: boolean;
  readonly hayDatos: boolean;
  readonly pctPotencial: number;
  readonly pctReal: number;
  readonly resultado: number;
  readonly coaching: CoachingUtilidad;
  readonly pctPrincipal: number;
  readonly hayUtilidad: boolean;
}

interface CoachingEnCursoProps {
  readonly pctPotencial: number;
  readonly pctReal: number;
  readonly resultado: number;
  readonly coaching: CoachingUtilidad;
}

function CoachingEnCurso({ pctPotencial, pctReal, resultado, coaching }: CoachingEnCursoProps): React.ReactElement {
  const barWidth = Math.min(100, pctPotencial > 0 ? (pctReal / pctPotencial) * 100 : 0);
  const pctRealTextClass = pctReal > 0 ? coaching.textClass : 'text-slate-700';
  const pctRealLabel = pctReal > 0 ? `${pctReal}%` : '0%';
  const resultadoColorClass = resultado > 0 ? coaching.textClass : 'text-amber-600';
  const resultadoSigno = resultado > 0 ? '+' : '';
  return (
    <>
      <div className="mb-2">
        <div className="relative h-1.5 rounded-full bg-black/10 overflow-hidden mb-2">
          <div
            className={`absolute left-0 top-0 h-full rounded-full transition-all duration-500 ${coaching.barFillClass}`}
            style={{ width: `${barWidth}%` }}
          />
        </div>
        <div className="flex items-baseline justify-between">
          <div>
            <span className={`text-2xl font-bold ${pctRealTextClass}`}>
              {pctRealLabel}
            </span>
            {resultado !== 0 && (
              <span className={`text-xs ml-1.5 ${resultadoColorClass}`}>
                {resultadoSigno}{formatCurrency(resultado)}
              </span>
            )}
          </div>
          <span className={`text-xs opacity-50 ${coaching.textClass}`}>
            meta {pctPotencial}%
          </span>
        </div>
      </div>
      {coaching.etiqueta && (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${coaching.badgeClass}`}>
          {coaching.etiqueta}
        </span>
      )}
    </>
  );
}

function CoachingPanel({ esFinalizada, hayDatos, pctPotencial, pctReal, resultado, coaching, pctPrincipal, hayUtilidad }: CoachingPanelProps): React.ReactElement {
  const pctPrincipalTextClass = pctPrincipal > 0 ? coaching.textClass : 'text-slate-800';
  const pctPrincipalLabel = pctPrincipal > 0 ? `${pctPrincipal}%` : '0%';
  return (
    <div className={`rounded-lg overflow-hidden border ${coaching.borderClass}`}>
      <div className={`p-3 ${coaching.bgClass}`}>
        <p className={`text-xs font-medium uppercase tracking-wide mb-2 ${coaching.textClass}`}>
          {esFinalizada ? 'Utilidad Final' : 'Utilidad'}
        </p>

        {!esFinalizada && hayDatos && pctPotencial > 0 ? (
          <CoachingEnCurso pctPotencial={pctPotencial} pctReal={pctReal} resultado={resultado} coaching={coaching} />
        ) : (
          <>
            <p className={`text-2xl font-bold ${pctPrincipalTextClass}`}>
              {pctPrincipalLabel}
            </p>
            {!hayUtilidad && resultado < 0 && esFinalizada && (
              <p className="text-xs text-red-700 mt-0.5">
                (Déficit: {formatCurrency(Math.abs(resultado))})
              </p>
            )}
            {hayDatos && coaching.etiqueta && (
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold mt-1 ${coaching.badgeClass}`}>
                {coaching.etiqueta}
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
}

interface ActividadesPageProps {
  readonly onViewDetalle?: (actividadId: string) => void;
}

interface ActividadCardProps {
  readonly actividad: ActividadEconomica;
  readonly onViewDetalle?: (actividadId: string) => void;
}

function ActividadCard({ actividad, onViewDetalle }: ActividadCardProps): React.ReactElement {
  const resultado = actividad.total_ingresos - actividad.total_costos;
  const hayUtilidad = resultado >= 0;
  const sumaTotal = actividad.total_costos + actividad.total_ingresos;
  const barCostoPct = sumaTotal > 0 ? (actividad.total_costos / sumaTotal) * 100 : 50;
  const barIngresosPct = sumaTotal > 0 ? (actividad.total_ingresos / sumaTotal) * 100 : 50;
  const esFinalizada = actividad.estado === 'finalizada' || actividad.estado === 'archivada';
  const ingresoMaximo = (actividad.utilidad_objetivo || 0) + actividad.total_costos;
  const pctPotencial = calcularPorcentajeUtilidadPotencial(ingresoMaximo, actividad.total_costos);
  const pctReal = actividad.porcentaje_utilidad;
  const hayDatos = actividad.total_costos > 0 || actividad.total_ingresos > 0;
  const pctCoaching = esFinalizada ? pctReal : pctPotencial;
  const coaching = getCoachingUtilidad(pctCoaching, hayDatos, esFinalizada ? resultado < 0 : ingresoMaximo < actividad.total_costos);
  const pctPrincipal = esFinalizada ? pctReal : pctPotencial;
  const esArchivada = actividad.estado === 'archivada';

  return (
    <div
      className={`relative bg-white border rounded-xl p-6 hover:shadow-lg transition-all group ${
        esArchivada ? 'border-amber-200 opacity-75' : 'border-slate-200'
      }`}
    >
      <button
        type="button"
        onClick={() => onViewDetalle?.(actividad.id)}
        className="absolute inset-0 rounded-xl cursor-pointer"
        aria-label={`Ver detalle de ${actividad.nombre}`}
      />
      <div className="relative pointer-events-none">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-xl font-bold text-slate-800 group-hover:text-indigo-600 transition-colors mb-2">
            {actividad.nombre}
          </h3>
          <StatusBadge estado={actividad.estado} tipo="actividad" />
        </div>
      </div>

      {actividad.descripcion && (
        <p className="text-sm text-slate-600 mb-4 line-clamp-2">
          {actividad.descripcion}
        </p>
      )}

      <div className="space-y-3 mb-4">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Calendar className="w-4 h-4" />
          <span>
            {formatDate(actividad.fecha_inicio)}
            {actividad.fecha_fin && ` - ${formatDate(actividad.fecha_fin)}`}
          </span>
        </div>

        {/* Barra bicolor compacta: Costos vs Ingresos */}
        <div>
          <div className="relative h-5 bg-slate-200 rounded-full overflow-hidden mb-2">
            <div
              className="absolute left-0 top-0 h-full bg-gradient-to-r from-red-400 to-red-500 transition-all duration-500"
              style={{ width: `${barCostoPct}%` }}
            ></div>
            <div
              className="absolute top-0 h-full bg-gradient-to-r from-emerald-400 to-emerald-500 transition-all duration-500"
              style={{ left: `${barCostoPct}%`, width: `${barIngresosPct}%` }}
            ></div>
          </div>
          <div className="flex justify-between text-xs">
            <div>
              <span className="text-red-600 font-medium">Gastos</span>
              <p className="text-sm font-bold text-red-800">{formatCurrency(actividad.total_costos)}</p>
            </div>
            <div className="text-right">
              <span className="text-emerald-600 font-medium">Ingresos</span>
              <p className="text-sm font-bold text-emerald-800">{formatCurrency(actividad.total_ingresos)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Utilidad con coaching */}
      <CoachingPanel
        esFinalizada={esFinalizada}
        hayDatos={hayDatos}
        pctPotencial={pctPotencial}
        pctReal={pctReal}
        resultado={resultado}
        coaching={coaching}
        pctPrincipal={pctPrincipal}
        hayUtilidad={hayUtilidad}
      />
      </div>
    </div>
  );
}

export const ActividadesPage: React.FC<ActividadesPageProps> = ({ onViewDetalle }) => {
  const { selectedLumisial } = useLumisial();
  const { usuario } = useAuth();
  // Crear actividades de este Lumisial: sus juntas locales y la excepción técnica global.
  const puedeOperar = puedeOperarDineroDeLumisial(usuario, selectedLumisial);
  const [actividades, setActividades] = useState<ActividadEconomica[]>([]);
  const [loading, setLoading] = useState(true);
  const [isActividadModalOpen, setIsActividadModalOpen] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState<'' | 'planificacion' | 'en_curso' | 'finalizada' | 'archivada'>('');
  const [mostrarArchivadas, setMostrarArchivadas] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 20;

  const loadActividades = async () => {
    if (!selectedLumisial) return;

    try {
      setLoading(true);
      // Si no hay filtro de estado explícito, excluir archivadas a menos que mostrarArchivadas esté activo
      const filtroEstado = estadoFiltro || undefined;
      const result = await actividadService.getPaginated(
        selectedLumisial.id,
        page,
        pageSize,
        filtroEstado as 'planificacion' | 'en_curso' | 'finalizada' | 'archivada' | undefined,
        busqueda,
        mostrarArchivadas || estadoFiltro === 'archivada'
      );
      setActividades(result.data);
      setTotalPages(result.totalPages);
      setTotal(result.total);
    } catch (error) {
      console.error('Error loading actividades:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedLumisial) {
      loadActividades();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLumisial, page, estadoFiltro, busqueda, mostrarArchivadas]);

  const resetFilters = () => {
    setBusqueda('');
    setEstadoFiltro('');
    setMostrarArchivadas(false);
    setPage(1);
  };

  const hasActiveFilters = !!(busqueda || estadoFiltro || mostrarArchivadas);


  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-800 mb-2">Actividades Económicas</h2>
          <p className="text-slate-600">Gestión de actividades generadoras de ingresos</p>
        </div>
        {puedeOperar && (
        <button
          onClick={() => setIsActividadModalOpen(true)}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold shadow-lg hover:shadow-xl"
        >
          <Plus className="w-5 h-5" />
          Nueva Actividad
        </button>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar por nombre o descripción..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-3 rounded-lg transition-colors font-semibold ${
              showFilters || hasActiveFilters
                ? 'bg-indigo-600 text-white'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <Filter className="w-5 h-5" />
            Filtros
            {hasActiveFilters && <span className="ml-2 bg-white/30 px-2 py-1 rounded text-xs">Activos</span>}
          </button>
        </div>

        {showFilters && (
          <div className="bg-slate-50 rounded-lg p-6 mb-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Select
                  label="Estado"
                  options={[
                    { value: 'planificacion', label: 'Planificación' },
                    { value: 'en_curso', label: 'En Curso' },
                    { value: 'finalizada', label: 'Finalizada' },
                    { value: 'archivada', label: 'Archivada' },
                  ]}
                  value={estadoFiltro}
                  onChange={(v) => setEstadoFiltro(v as '' | 'planificacion' | 'en_curso' | 'finalizada' | 'archivada')}
                  placeholder="Todos"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={mostrarArchivadas}
                  onChange={(e) => { setMostrarArchivadas(e.target.checked); setPage(1); }}
                  className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                />
                <span className="text-sm font-medium text-slate-700 flex items-center gap-1">
                  <Archive className="w-4 h-4 text-amber-600" />
                  Mostrar archivadas
                </span>
              </label>
            </div>

            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-800 font-medium text-sm"
              >
                <X className="w-4 h-4" />
                Limpiar filtros
              </button>
            )}
          </div>
        )}

        {loading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-700"></div>
          </div>
        )}

        {!loading && actividades.length === 0 && (
          <div className="text-center py-12">
            <Briefcase className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 mb-2">No hay actividades registradas</p>
            {hasActiveFilters && <p className="text-sm text-slate-500">Intenta cambiar los filtros</p>}
            {!hasActiveFilters && puedeOperar && (
              <button
                onClick={() => setIsActividadModalOpen(true)}
                className="mt-4 text-blue-600 hover:underline font-medium"
              >
                Crea la primera actividad
              </button>
            )}
          </div>
        )}

        {!loading && actividades.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {actividades.map((actividad: ActividadEconomica) => (
              <ActividadCard
                key={actividad.id}
                actividad={actividad}
                onViewDetalle={onViewDetalle}
              />
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            <span className="text-sm text-slate-600">
              Página {page} de {totalPages} ({total} actividades)
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Siguiente
            </button>
          </div>
        )}
      </div>

      <ActividadModal
        isOpen={isActividadModalOpen}
        onClose={() => setIsActividadModalOpen(false)}
        onSuccess={loadActividades}
      />
    </div>
  );
};
